<template>
  <v-col>
    <v-row>
      <v-col class="col-auto">
        <v-row>
          <v-col class="col-auto">
            <span class="subtitle-2 font-weight-bold">Enterprise</span>
          </v-col>

          <v-col class="col-auto pt-2 pl-1">
            <v-btn
              color="primary"
              dense
              small
              icon
              :loading="isLoading"
              @click="fetchEnterpriseData"
            >
              <font-awesome-icon
                :icon="['fas', 'sync']"
                size="lg"
              />
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption">View and manage your enterprise and organizations' settings.</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Divider -->
    <v-row>
      <v-col>
        <v-divider />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <span class="subtitle-2 font-weight-bold">Profile</span>
      </v-col>
    </v-row>

    <!-- Enterprise Name -->
    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Name</span>
          </v-col>

          <v-col
            v-if="currentUser.hasCustomRoleDev"
            class="col-auto pl-0"
          >
            <!-- Text Button: Add Name -->
            <v-btn
              color="primary elevation-0"
              text
              x-small
              @click="promptToUpdateEnterpriseName"
            >
              <span class="caption">Edit</span>
            </v-btn>

            <v-dialog
              v-model="showViewToUpdateEnterpriseName"
              max-width="500"
            >
              <v-card>
                <v-card-title>
                  <v-col>
                    <span class="subtitle-2 font-weight-bold">Update Enterprise Name</span>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <v-btn
                      icon
                      @click="cancelUpdateEnterpriseName"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'times']"
                        size="lg"
                      />
                    </v-btn>
                  </v-col>
                </v-card-title>

                <v-card-text>
                  <v-col>
                    <v-row>
                      <v-col class="pa-0">
                        <v-form>
                          <v-col>
                            <v-row>
                              <v-col>
                                <v-text-field
                                  v-model="inputEnterpriseName"
                                  label="Enterprise Name"
                                  required
                                  outlined
                                  :rules="[
                                    v => !!v || 'Enterprise name is required',
                                    v => /^[a-zA-Z\s-'’]+$/.test(v) || 'Enterprise name must contain only letters, spaces, hyphens, or apostrophes'
                                  ]"
                                />
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-form>
                      </v-col>
                    </v-row>

                    <!-- Error Message -->
                    <v-row v-if="errorUpdatingEnterpriseName">
                      <v-col>
                        <span class="error--text">{{ errorUpdatingEnterpriseName }}</span>
                      </v-col>
                    </v-row>

                    <!-- Success Message -->
                    <v-row v-if="successUpdatingEnterpriseName">
                      <v-col>
                        <span class="success--text">Enterprise name updated successfully.</span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-card-text>

                <v-card-actions>
                  <v-col class="col-auto">
                    <!-- Cancel -->
                    <v-btn
                      color="primary"
                      text
                      @click="cancelUpdateEnterpriseName"
                    >
                      Cancel
                    </v-btn>
                  </v-col>

                  <v-spacer />

                  <v-col class="col-auto">
                    <!-- "Update Name" Button -->
                    <v-btn
                      color="primary"
                      @click="updateEnterpriseName"
                    >
                      Update
                    </v-btn>
                  </v-col>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span
              v-if="enterprise.enterpriseName"
              class="caption font-weight-medium"
            >{{ enterprise.enterpriseName }}</span>

            <span
              v-else
              class="caption font-weight-medium"
            >No enterprise name set.</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Tenant Information -->
    <v-row>
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Tenant</span>
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col class="pt-0">
            <span class="caption font-weight-medium">{{ domain.shortName || 'No tenants found.' }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Enterprise Domains -->
    <v-row v-if="enterprise && Array.isArray(enterprise.domains)">
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Domains</span>
          </v-col>
        </v-row>

        <v-row
          v-if="enterprise.domains && enterprise.domains.length"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span
              v-for="(domainString, index) in enterprise.domains"
              :key="index"
              class="caption font-weight-medium"
            >{{ domainString }} </span>
          </v-col>
        </v-row>

        <v-row
          v-else
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-medium">No domains found.</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Billing Codes -->
    <v-row v-if="currentUser && Array.isArray(currentUser.billingCodes)">
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Organization Codes</span>
          </v-col>
        </v-row>

        <v-row
          v-if="currentUser.billingCodes && currentUser.billingCodes.length"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span
              v-for="(code, index) in currentUser.billingCodes"
              :key="index"
              class="caption font-weight-medium"
            >{{ code }} </span>
          </v-col>
        </v-row>

        <v-row
          v-else
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-medium">No organization codes found.</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- User Networks -->
    <v-row v-if="currentUser && Array.isArray(currentUser.network)">
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="subtitle-2 font-weight-bold">Networks</span>
          </v-col>
        </v-row>

        <v-row
          v-if="currentUser.network && currentUser.network.length"
          class="mt-0"
        >
          <v-card
            v-for="(network, networkIndex) in currentUser.network"
            :key="networkIndex"
          >
            <v-col class="pt-0">
              <v-row
                class="caption font-weight-medium mt-0"
              >
                <v-col class="col-auto">
                  <v-card min-width="480px">
                    <v-card-title>
                      <v-col class="col-auto pt-2">
                        <!-- Active Status -->
                        <font-awesome-icon
                          :icon="network.publisher.isActive ? ['fas', 'check-circle'] : ['fas', 'times-circle']"
                          :class="network.publisher.isActive ? 'grassgreen--text' : 'error--text'"
                        />
                      </v-col>

                      <v-col class="col-auto pt-2">
                        <span class="subtitle-2 font-weight-bold">
                          {{ network.publisher.name }}
                        </span>
                      </v-col>
                    </v-card-title>

                    <v-card-text>
                      <v-col class="col-auto pt-2">
                        <v-row>
                          <v-col class="col-auto">
                            <span class="caption font-weight-bold">Network Code</span>
                          </v-col>

                          <v-col class="col-auto">
                            <span class="caption font-weight-medium">{{ network.publisher.networkCode }}</span>
                          </v-col>
                        </v-row>

                        <!-- Timezone -->
                        <v-row>
                          <v-col class="col-auto">
                            <span class="caption font-weight-bold">Timezone</span>
                          </v-col>

                          <v-col class="col-auto">
                            <span class="caption font-weight-medium">{{ network.publisher.timeZone }}</span>
                          </v-col>
                        </v-row>

                        <!-- Ad Server URL -->
                        <v-row>
                          <v-col class="col-auto">
                            <span class="caption font-weight-bold">Ad Server URL</span>
                          </v-col>

                          <v-col class="col-auto">
                            <a
                              :href="network.publisher.adServerUrl"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span class="caption font-weight-medium">{{ network.publisher.adServerUrl }}</span>
                            </a>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-card>
        </v-row>
      </v-col>
    </v-row>

    <v-row
      v-else
      class="mt-0"
    >
      <v-col class="pt-0">
        <span class="caption font-weight-medium">No networks found.</span>
      </v-col>
    </v-row>

    <!-- isGamTeamnabled -->

    <!-- Providers -->
    <v-row v-if="domain">
      <v-col>
        <v-row>
          <v-col class="col-auto">
            <span class="caption font-weight-bold">Providers</span>
          </v-col>
        </v-row>

        <v-row
          v-if="domain.providers && domain.providers.length"
          class="mt-0"
        >
          <v-col class="pt-0">
            <v-row
              v-for="(provider, index) in domain.providers"
              :key="index"
              class="caption font-weight-medium mt-0"
            >
              <v-col class="col-auto pt-2">
                <!-- Active Status -->
                <font-awesome-icon
                  :icon="provider.isActive ? ['fas', 'check-circle'] : ['fas', 'times-circle']"
                  :class="provider.isActive ? 'grassgreen--text' : 'error--text'"
                />
              </v-col>

              <v-col class="col-auto pt-2 pl-0">
                <span>{{ provider.providerId }}</span>
              </v-col>

              <v-col class="col-auto pt-2 pl-0">
                <!-- isPrimary -->
                <span
                  v-if="provider.isPrimary"
                  class="caption font-weight-medium"
                > (Primary)</span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row
          v-else
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-medium">No providers found.</span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { fireauth } from '@/store/firebase.js'

export default {
  name: 'AdminEnterprise',
  data() {
    return {
      currentUser: {},
      domain: {},
      enterprise: {},
      isLoading: true,

      errorUpdatingEnterpriseName: null,
      inputEnterpriseName: '',
      showViewToUpdateEnterpriseName: false,
      successUpdatingEnterpriseName: false,
    }
  },
  created() {
    this.fetchEnterpriseData()
  },
  methods: {

    async fetchEnterpriseData() {
      const logger = console

      try {
        const response = await this.$store.dispatch('admin/fetchEnterpriseData')

        if (!response || response.error) {
          throw new Error('No enterprise data found')
        }

        const { currentUser, domain, enterprise } = response

        // Set the view's data.
        this.currentUser = currentUser
        this.domain = domain
        this.enterprise = enterprise
      } catch (error) {
        logger.error('Error fetching enterprise data:', error)
      } finally {
        this.isLoading = false
      }
    },

    promptToUpdateEnterpriseName() {
      this.inputEnterpriseName = this.enterprise.name || ''
      this.showViewToUpdateEnterpriseName = true
    },

    cancelUpdateEnterpriseName() {
      this.showViewToUpdateEnterpriseName = false
      this.inputEnterpriseName = ''
      this.errorUpdatingEnterpriseName = null
      this.successUpdatingEnterpriseName = false
    },

    async updateEnterpriseName() {
      const logger = console

      const getUid = () => {
        const currentUser = fireauth.getCurrentUser()
        return currentUser?.uid
      }

      try {
        const committerEmail = this.currentUser.email
        const committerUid = this.currentUser.uid || getUid()
        const { docId } = this.enterprise
        const enterpriseName = this.inputEnterpriseName.trim()

        const response = await this.$store.dispatch('admin/updateEnterpriseName', {
          committerEmail,
          committerUid,
          docId,
          enterpriseName,
        })

        if (!response || response.error) {
          throw new Error('Failed to update enterprise name')
        }

        // Update the local enterprise data
        this.enterprise.name = this.inputEnterpriseName
      } catch (error) {
        logger.error('Error updating enterprise name:', error)
      }
    },
  },
}
</script>
